import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Description as WordIcon
} from '@mui/icons-material';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';

// Estilos compartidos con TiptapEditor para que el preview/print se vean igual
// que en el editor (mismas medidas de heading, márgenes, alineación de imagen
// vía `data-align`, y chip azul para variables `<span data-variable>`).
const documentContentSx = {
  fontFamily: 'Arial, sans-serif',
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#333',
  '& p': { margin: '0 0 0.75em' },
  '& h1': { fontSize: '1.6em', fontWeight: 700, margin: '0.5em 0' },
  '& h2': { fontSize: '1.35em', fontWeight: 700, margin: '0.5em 0' },
  '& h3': { fontSize: '1.15em', fontWeight: 700, margin: '0.5em 0' },
  '& ul, & ol': { paddingLeft: '1.5em' },
  '& img': { maxWidth: '100%', height: 'auto' },
  '& img[data-align="center"]': { display: 'block', margin: '0.5em auto' },
  '& img[data-align="right"]': { display: 'block', margin: '0.5em 0 0.5em auto' },
  '& img[data-align="left"]': { display: 'block', margin: '0.5em auto 0.5em 0' },
  '& [data-variable]': {
    display: 'inline-block',
    padding: '0 6px',
    height: 22,
    lineHeight: '20px',
    verticalAlign: 'middle',
    fontSize: '0.78rem',
    fontWeight: 600,
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #1976d2',
    borderRadius: 11,
    margin: '0 2px'
  }
};

// Mismo bloque pero como CSS plano para inyectar en la ventana de impresión.
const documentContentCss = `
  body {
    font-family: Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    margin: 20px;
  }
  p { margin: 0 0 0.75em; }
  h1 { font-size: 1.6em; font-weight: 700; margin: 0.5em 0; }
  h2 { font-size: 1.35em; font-weight: 700; margin: 0.5em 0; }
  h3 { font-size: 1.15em; font-weight: 700; margin: 0.5em 0; }
  ul, ol { padding-left: 1.5em; }
  img { max-width: 100%; height: auto; }
  img[data-align="center"] { display: block; margin: 0.5em auto; }
  img[data-align="right"]  { display: block; margin: 0.5em 0 0.5em auto; }
  img[data-align="left"]   { display: block; margin: 0.5em auto 0.5em 0; }
  [data-variable] {
    display: inline-block;
    padding: 0 6px;
    height: 22px;
    line-height: 20px;
    vertical-align: middle;
    font-size: 0.78rem;
    font-weight: 600;
    background-color: #e3f2fd;
    color: #1976d2;
    border: 1px solid #1976d2;
    border-radius: 11px;
    margin: 0 2px;
  }
  @media print {
    /* Margenes simetricos via @page para que el contrato luzca igual en
       anverso y reverso cuando el usuario imprime a doble cara desde el
       dialogo del navegador. */
    @page { size: letter; margin: 18mm; }
    body { margin: 0; }

    /* Evitar lineas sueltas al inicio/fin de hoja. */
    p, li { orphans: 3; widows: 3; }

    /* No dejar un titulo huerfano al final de una hoja. */
    h1, h2, h3, h4, h5, h6 {
      page-break-after: avoid;
      break-after: avoid;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    /* No partir tablas, imagenes ni figuras entre paginas. */
    table, figure, img {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    tr, td, th { page-break-inside: avoid; break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    /* Utilidades para plantillas: forzar salto de hoja o impedir corte. */
    .page-break, [data-page-break="true"] {
      page-break-before: always;
      break-before: page;
    }
    .no-break, [data-no-break="true"] {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    [data-variable] {
      background: transparent;
      border: 0;
      color: inherit;
      padding: 0;
      font-weight: inherit;
    }
  }
`;

const DocumentPreview = ({ open, onClose, plantilla }) => {
  const [previewContent, setPreviewContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  useEffect(() => {
    if (open && plantilla) {
      setPreviewContent(plantilla.contenido_html || '');
      setEditedContent(plantilla.contenido_html || '');
      setIsEditing(false);
    }
  }, [open, plantilla]);

  // Genera y descarga el documento como archivo .doc abrible directamente por
  // Microsoft Word / Google Docs / LibreOffice. Cuando el contenido tiene
  // imagenes, las embebemos como partes MHTML (multipart/related) porque Word
  // ignora las imagenes en `data:image/...;base64,...` directamente en <img>.
  // El formato MHTML es exactamente lo que produce Word al "Guardar como
  // pagina web de archivo unico", asi que es 100% compatible.
  const handleDownloadWord = async () => {
    try {
      const safeName = (plantilla?.nombre || 'documento')
        .replace(/[\\/:*?"<>|]+/g, '_')
        .replace(/\s+/g, '_')
        .trim() || 'documento';

      const extFromMime = (mime) => {
        const m = (mime || '').toLowerCase();
        if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
        if (m.includes('png')) return 'png';
        if (m.includes('gif')) return 'gif';
        if (m.includes('webp')) return 'webp';
        if (m.includes('svg')) return 'svg';
        return 'bin';
      };

      // 1) Recorrer las <img> del contenido. Cada imagen (sea data:URL o
      //    http(s)://) la convertimos a base64, le asignamos un nombre tipo
      //    `image001.png` y reemplazamos el src del <img> por ese nombre.
      //    Ademas, propagamos `data-align` a estilos inline + atributo `align`
      //    en el <p>/<div> contenedor, porque Word ignora los selectores de
      //    atributo CSS (img[data-align="center"]) y termina alineando todo a
      //    la izquierda.
      const parser = new DOMParser();
      const doc = parser.parseFromString(previewContent || '', 'text/html');
      const imgs = Array.from(doc.querySelectorAll('img'));

      const appendStyle = (el, css) => {
        const prev = (el.getAttribute('style') || '').trim();
        const sep = prev && !prev.endsWith(';') ? ';' : '';
        el.setAttribute('style', `${prev}${sep}${css}`);
      };

      const applyAlignment = (img, align) => {
        const wrapper = img.closest('p, div, figure');
        if (align === 'center') {
          appendStyle(img, 'display:block;margin-left:auto;margin-right:auto;');
          if (wrapper) {
            wrapper.setAttribute('align', 'center');
            appendStyle(wrapper, 'text-align:center;');
          }
        } else if (align === 'right') {
          appendStyle(img, 'display:block;margin-left:auto;margin-right:0;');
          if (wrapper) {
            wrapper.setAttribute('align', 'right');
            appendStyle(wrapper, 'text-align:right;');
          }
        } else if (align === 'left') {
          appendStyle(img, 'display:block;margin-left:0;margin-right:auto;');
          if (wrapper) {
            wrapper.setAttribute('align', 'left');
            appendStyle(wrapper, 'text-align:left;');
          }
        }
      };

      const imageParts = [];
      let counter = 0;

      for (const img of imgs) {
        applyAlignment(img, img.getAttribute('data-align'));

        const src = img.getAttribute('src') || '';

        if (src.startsWith('data:image/')) {
          const match = src.match(/^data:(image\/[a-zA-Z0-9+.-]+);base64,(.+)$/);
          if (!match) continue;
          counter += 1;
          const mime = match[1];
          const base64 = match[2];
          const cid = `image${String(counter).padStart(3, '0')}.${extFromMime(mime)}`;
          imageParts.push({ cid, mime, base64 });
          img.setAttribute('src', cid);
        } else if (/^https?:\/\//i.test(src)) {
          // Imagen referenciada por URL: intentar bajarla y embeberla. Si CORS
          // falla, dejamos la URL tal cual (Word intentara bajarla al abrir).
          try {
            const resp = await fetch(src);
            const blob = await resp.blob();
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result || '';
                const idx = String(result).indexOf(',');
                resolve(idx >= 0 ? String(result).slice(idx + 1) : '');
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            counter += 1;
            const mime = blob.type || 'image/png';
            const cid = `image${String(counter).padStart(3, '0')}.${extFromMime(mime)}`;
            imageParts.push({ cid, mime, base64 });
            img.setAttribute('src', cid);
          } catch (e) {
            console.warn('No se pudo embeber imagen externa:', src, e);
          }
        }
      }

      // CSS calibrado en pt (Word interpreta px de forma rara).
      const wordCss = `
        @page WordSection1 {
          size: 8.5in 11.0in;
          margin: 1.0in 1.0in 1.0in 1.0in;
        }
        div.WordSection1 { page: WordSection1; }
        body {
          font-family: Arial, sans-serif;
          font-size: 12pt;
          line-height: 1.5;
          color: #000;
        }
        p { margin: 0 0 8pt; }
        h1 { font-size: 18pt; font-weight: bold; margin: 12pt 0 6pt; }
        h2 { font-size: 15pt; font-weight: bold; margin: 12pt 0 6pt; }
        h3 { font-size: 13pt; font-weight: bold; margin: 10pt 0 6pt; }
        ul, ol { margin: 0 0 8pt 24pt; }
        table { border-collapse: collapse; }
        td, th { padding: 4pt 6pt; }
        img { max-width: 100%; height: auto; }
        img[data-align="center"] { display: block; margin: 6pt auto; }
        img[data-align="right"]  { display: block; margin: 6pt 0 6pt auto; }
        img[data-align="left"]   { display: block; margin: 6pt auto 6pt 0; }
        [data-variable] {
          background: transparent;
          border: 0;
          color: inherit;
          padding: 0;
          font-weight: inherit;
        }
      `;

      const htmlBody = doc.body ? doc.body.innerHTML : (previewContent || '');

      const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8">
<title>${plantilla?.nombre || 'Documento'}</title>
<!--[if gte mso 9]>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
  </w:WordDocument>
</xml>
<![endif]-->
<style>${wordCss}</style>
</head>
<body>
<div class="WordSection1">
${htmlBody}
</div>
</body>
</html>`;

      let blob;
      if (imageParts.length === 0) {
        // Sin imagenes: HTML simple alcanza, no hace falta MHTML.
        blob = new Blob(['\ufeff', '<!DOCTYPE html>\n', html], {
          type: 'application/msword'
        });
      } else {
        // Construir un MHTML (multipart/related) con el HTML como parte raiz
        // y una parte por cada imagen referenciada por Content-Location.
        const boundary =
          '----=_NextPart_Bankivo_' +
          Date.now().toString(36) +
          Math.random().toString(36).slice(2, 10);
        const CRLF = '\r\n';
        const wrapBase64 = (b64) =>
          b64.replace(/\s+/g, '').match(/.{1,76}/g).join(CRLF);

        const chunks = [];
        chunks.push(
          'MIME-Version: 1.0',
          'Content-Type: multipart/related; type="text/html"; boundary="' +
            boundary +
            '"',
          '',
          '--' + boundary,
          'Content-Type: text/html; charset="utf-8"',
          'Content-Transfer-Encoding: 8bit',
          'Content-Location: index.html',
          '',
          html,
          ''
        );

        for (const part of imageParts) {
          chunks.push(
            '--' + boundary,
            'Content-Type: ' + part.mime,
            'Content-Transfer-Encoding: base64',
            'Content-Location: ' + part.cid,
            '',
            wrapBase64(part.base64),
            ''
          );
        }

        chunks.push('--' + boundary + '--', '');

        const mhtml = chunks.join(CRLF);
        blob = new Blob([mhtml], { type: 'application/msword' });
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setMessageAlert('Documento descargado como Word.');
      setAlertType('success');
      setOpenAlert(true);
    } catch (err) {
      console.error('Error generando Word:', err);
      setMessageAlert('No se pudo generar el archivo de Word.');
      setAlertType('error');
      setOpenAlert(true);
    }
  };

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setMessageAlert('No se pudo abrir la ventana de impresión. Revisa el bloqueador de pop-ups.');
      setAlertType('error');
      setOpenAlert(true);
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${plantilla?.nombre || 'Documento'}</title>
          <meta charset="UTF-8">
          <style>${documentContentCss}</style>
        </head>
        <body>
          ${previewContent}
        </body>
      </html>
    `);
    printWindow.document.close();

    // Las imágenes (incluso base64) se decodifican de forma asíncrona. Si
    // disparamos `print()` antes de que terminen, salen en blanco. Esperamos
    // a que todas las <img> estén cargadas y recién ahí imprimimos.
    const waitForImages = () => {
      const imgs = Array.from(printWindow.document.images || []);
      if (imgs.length === 0) return Promise.resolve();
      return Promise.all(
        imgs.map((img) => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        })
      );
    };

    const launchPrint = async () => {
      try {
        await waitForImages();
      } catch (_) { /* ignorar fallos de imagen, dejamos imprimir igual */ }

      printWindow.focus();

      let closed = false;
      const safeClose = () => {
        if (closed) return;
        closed = true;
        try { printWindow.close(); } catch (_) { /* noop */ }
      };
      // Cerrar al terminar (cancelar o aceptar) la impresión.
      printWindow.onafterprint = safeClose;
      // Fallback por si `onafterprint` no dispara (Safari en algunas versiones).
      setTimeout(safeClose, 60000);

      printWindow.print();
    };

    if (printWindow.document.readyState === 'complete') {
      launchPrint();
    } else {
      printWindow.addEventListener('load', launchPrint, { once: true });
    }
  };

  // Handle close
  const handleClose = () => {
    setPreviewContent('');
    setEditedContent('');
    setIsEditing(false);
    onClose();
  };

  const handleToggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  const handleSaveEdits = () => {
    setPreviewContent(editedContent || '');
    setIsEditing(false);
    setMessageAlert('Cambios guardados.');
    setAlertType('success');
    setOpenAlert(true);
  };

  const handleResetToOriginal = () => {
    const original = plantilla?.contenido_html || '';
    setEditedContent(original);
    setPreviewContent(original);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Vista Previa: {plantilla?.nombre || 'Plantilla'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isEditing ? (
                <>
                  <Button
                    startIcon={<SaveIcon />}
                    onClick={handleSaveEdits}
                    variant="contained"
                    size="small"
                  >
                    Guardar cambios
                  </Button>
                  <Button
                    onClick={handleToggleEdit}
                    variant="outlined"
                    size="small"
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={handleToggleEdit}
                    variant="outlined"
                    size="small"
                  >
                    Editar
                  </Button>
                  <Button
                    startIcon={<WordIcon />}
                    onClick={handleDownloadWord}
                    variant="outlined"
                    size="small"
                  >
                    Descargar Word
                  </Button>
                  <Button
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    variant="outlined"
                    size="small"
                  >
                    Imprimir
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Template Info */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Información de la Plantilla
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Nombre:</strong> {plantilla?.nombre}
                  </Typography>
                  {plantilla?.descripcion && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Descripción:</strong> {plantilla.descripcion}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tipo:</strong> {plantilla?.tipo === 'contrato_prestamo' ? 'Contrato de Préstamo' : plantilla?.tipo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Estado:</strong> {plantilla?.activo ? 'Activo' : 'Inactivo'}
                  </Typography>
                </CardContent>
              </Card>

              <Divider sx={{ my: 2 }} />

              {/* Preview / Edit Content */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {isEditing ? 'Editar contenido' : 'Contenido de la Plantilla'}
                </Typography>
                {isEditing ? (
                  <>
                    <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={handleResetToOriginal}>
                        Restablecer original
                      </Button>
                    </Box>
                    <Box
                      contentEditable
                      suppressContentEditableWarning
                      onInput={(e) => setEditedContent(e.currentTarget.innerHTML)}
                      dangerouslySetInnerHTML={{ __html: editedContent || '' }}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 3,
                        backgroundColor: '#fff',
                        minHeight: '400px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        outline: 'none',
                        ...documentContentSx
                      }}
                    />
                  </>
                ) : (
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: previewContent || '<p>No hay contenido para mostrar</p>'
                    }}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 3,
                      backgroundColor: '#fafafa',
                      minHeight: '400px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      ...documentContentSx
                    }}
                  />
                )}
              </Box>

              {/* Variables Info */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Nota sobre Variables
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Las variables como <code>{'{{cliente_nombre}}'}</code>, <code>{'{{monto_prestamo}}'}</code>, etc. 
                  se reemplazarán automáticamente con los datos reales del préstamo cuando se genere el documento.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} startIcon={<CloseIcon />}>
            Cerrar
          </Button>
          <Button
            onClick={handleDownloadWord}
            variant="outlined"
            startIcon={<WordIcon />}
            sx={{
              borderColor: '#55b4ac',
              color: '#18586b',
              '&:hover': {
                borderColor: '#18586b',
                backgroundColor: 'rgba(85, 180, 172, 0.08)'
              }
            }}
          >
            Descargar Word
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{ 
              backgroundColor: '#55b4ac',
              '&:hover': {
                backgroundColor: '#18586b'
              }
            }}
          >
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={messageAlert}
        severity={alertType}
        title=""
      />
    </>
  );
};

export default DocumentPreview;
